import { api, handleApiError } from '../client'

/**
 * Types for Bin (Trash) items
 */
export type BinItemType = 'template' | 'asset'

export interface BinItem {
  id: string
  originalId: string
  name: string
  type: BinItemType
  deletedAt: string
  deletedBy: string
  deletedByName?: string
  size?: number
  thumbnailUrl?: string
  expiresAt: string
  metadata?: {
    templateType?: 'Word' | 'Excel' | 'PowerPoint' | 'Outlook'
    assetType?: string
    description?: string
  }
}

export interface BinListResponse {
  items: BinItem[]
  totalCount: number
  templateCount: number
  assetCount: number
}

export interface BinCountResponse {
  total: number
  templates: number
  assets: number
}

export const binService = {
  /**
   * Gets all items in the bin with optional type filter
   */
  async getItems(options?: {
    type?: BinItemType | 'all'
    page?: number
    pageSize?: number
    sortField?: string
    sortDirection?: 'asc' | 'desc'
  }): Promise<BinListResponse> {
    try {
      const params = new URLSearchParams()

      if (options?.type && options.type !== 'all') {
        params.append('type', options.type)
      }
      if (options?.page) {
        params.append('page', String(options.page))
      }
      if (options?.pageSize) {
        params.append('pageSize', String(options.pageSize))
      }
      if (options?.sortField) {
        params.append('sortField', options.sortField)
      }
      if (options?.sortDirection) {
        params.append('sortDirection', options.sortDirection)
      }

      const queryString = params.toString() ? `?${params.toString()}` : ''
      const response = await api.get<BinListResponse>(`/v1/bin${queryString}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets count of items in bin by type
   */
  async getCount(): Promise<BinCountResponse> {
    try {
      const response = await api.get<BinCountResponse>('/v1/bin/count')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Restores a single item from the bin
   */
  async restoreItem(type: BinItemType, id: string): Promise<void> {
    try {
      await api.post(`/v1/bin/${type}/${id}/restore`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Restores multiple items from the bin
   */
  async restoreMultiple(items: Array<{ type: BinItemType; id: string }>): Promise<void> {
    try {
      await api.post('/v1/bin/restore', { items })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Permanently deletes a single item from the bin
   */
  async deleteItem(type: BinItemType, id: string): Promise<void> {
    try {
      await api.delete(`/v1/bin/${type}/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Permanently deletes multiple items from the bin
   */
  async deleteMultiple(items: Array<{ type: BinItemType; id: string }>): Promise<void> {
    try {
      await api.post('/v1/bin/delete', { items })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Empties the entire bin (permanently deletes all items)
   */
  async emptyBin(): Promise<void> {
    try {
      await api.delete('/v1/bin/empty')
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Utility function to format file size
   */
  formatFileSize(bytes?: number): string {
    if (!bytes || bytes === 0) return '-'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  },

  /**
   * Calculate days until permanent deletion
   */
  getDaysUntilExpiration(expiresAt: string): number {
    const now = new Date()
    const expiration = new Date(expiresAt)
    const diffTime = expiration.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return Math.max(0, diffDays)
  },
}
